import { v4 as uuidv4 } from 'uuid';
import {
  Criterion,
  Field,
  FieldJump,
  FieldType,
  IaScreenerChoice,
  IaScreenerChoiceStatus,
  IaScreenerQuestion,
  ListValueType,
  LogicJumpActionType,
  SelectionBehavior,
} from '@app/api';

export interface FieldWithCriterion {
  field: Field;
  criterion?: Criterion;
}

export const END_JUMP = 'END';

type JumpAction = Pick<FieldJump, 'type' | 'toFieldId'>;

const FIELD_TYPES: Record<IaScreenerQuestion['type'], FieldType> = {
  SINGLE_CHOICE: FieldType.SINGLE_CHOICE,
  MULTIPLE_CHOICE: FieldType.MULTIPLE_CHOICE,
  SHORT_TEXT: FieldType.SHORT_TEXT,
  LONG_TEXT: FieldType.LONG_TEXT,
  INFORMATION_PAGE: FieldType.INFORMATION_PAGE,
};

export const isIaChoiceQuestion = (question: IaScreenerQuestion) =>
  question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE';

export const getIaChoiceStatus = (
  choice: IaScreenerChoice,
): IaScreenerChoiceStatus => choice.status || 'NEUTRAL';

export type IaChoiceState = 'qualify' | 'tolerate' | 'none' | 'disqualify';

/**
 * The state the builder will show for this choice once the suggestion is
 * applied: a "NEUTRAL" choice is tolerated on a multiple choice that has "OK"
 * choices, and has no state elsewhere.
 */
export const getIaChoiceState = (
  question: IaScreenerQuestion,
  choice: IaScreenerChoice,
): IaChoiceState => {
  const status = getIaChoiceStatus(choice);
  if (status === 'OK') {
    return 'qualify';
  }
  if (status === 'KO') {
    return 'disqualify';
  }
  const hasQualifying = (question.choices || []).some(
    (other) => getIaChoiceStatus(other) === 'OK',
  );

  return question.type === 'MULTIPLE_CHOICE' && hasQualifying
    ? 'tolerate'
    : 'none';
};

/**
 * The questions the screener can show: a choice question needs at least two
 * choices, and an unknown type is dropped.
 */
export const getDisplayableIaQuestions = (questions: IaScreenerQuestion[]) =>
  questions.filter(
    (question) =>
      question.title &&
      FIELD_TYPES[question.type] &&
      (!isIaChoiceQuestion(question) || (question.choices?.length || 0) > 1),
  );

/**
 * Converts the questions suggested by the IA into screener fields and their
 * criteria, as the builder would have made them:
 * - "OK" choices are allowed and accepted automatically, "KO" ones refused,
 *   and a question without "OK" nor "KO" choice has no criterion;
 * - "NEUTRAL" choices are allowed on a single choice. On a multiple choice with
 *   "OK" choices they are tolerated (left out of the allow list): they pass
 *   only next to an "OK" one. Without "OK" choice, a multiple choice keeps no
 *   allow list, which would otherwise reject everybody;
 * - the jumps of the choices are grouped by destination, and the jump of the
 *   question becomes the default jump (without condition). A jump to a
 *   question that is not converted is dropped: the screener then goes on.
 * Only information pages can show a description, so the other questions get
 * it under their title.
 */
export const buildScreenerFromIaQuestions = (
  questions: IaScreenerQuestion[],
): FieldWithCriterion[] => {
  const displayable = getDisplayableIaQuestions(questions);
  const fieldIds = new Map(
    displayable.map((question) => [question.id, uuidv4()]),
  );

  return displayable.map((question) => {
    const id = fieldIds.get(question.id) as string;
    const ref = uuidv4();
    const choices = (question.choices || []).map((choice) => ({
      ...choice,
      status: getIaChoiceStatus(choice),
      ref: uuidv4(),
    }));
    const jumpActions = buildJumpActions(id, question.jump, choices, fieldIds);

    if (question.type === 'INFORMATION_PAGE') {
      return {
        field: {
          id,
          ref,
          title: question.title,
          type: FieldType.INFORMATION_PAGE,
          properties: { description: question.description || '' },
          jumpActions,
        },
      };
    }

    const base = {
      id,
      ref,
      title: question.description
        ? `${question.title}\n${question.description}`
        : question.title,
      validation: { required: true },
      jumpActions,
    };

    if (question.type === 'SHORT_TEXT' || question.type === 'LONG_TEXT') {
      return {
        field: {
          ...base,
          type:
            question.type === 'SHORT_TEXT'
              ? FieldType.SHORT_TEXT
              : FieldType.LONG_TEXT,
        },
      };
    }

    const type =
      question.type === 'MULTIPLE_CHOICE'
        ? FieldType.MULTIPLE_CHOICE
        : FieldType.SINGLE_CHOICE;
    const field = {
      ...base,
      type,
      properties: {
        randomize: false,
        allowMultipleSelection: type === FieldType.MULTIPLE_CHOICE,
        autocomplete: false,
        choices: choices.map((choice) => ({
          ref: choice.ref,
          label: choice.label,
        })),
      },
    } as Field;

    const refsWith = (statuses: IaScreenerChoiceStatus[]) =>
      choices
        .filter((choice) => statuses.includes(choice.status))
        .map((choice) => choice.ref);
    const acceptedRefs = refsWith(['OK']);
    const refusedRefs = refsWith(['KO']);
    if (!acceptedRefs.length && !refusedRefs.length) {
      return { field };
    }

    const criterion: Criterion = {
      id: uuidv4(),
      fieldRef: ref,
      screenerCriterion: true,
      selectionBehavior: SelectionBehavior.OPTIONAL,
      allowList:
        type === FieldType.SINGLE_CHOICE
          ? refsWith(['OK', 'NEUTRAL'])
          : acceptedRefs.length
          ? acceptedRefs
          : null,
      automaticlyAcceptedList: acceptedRefs,
      listValueType: ListValueType.KEYWORD,
      denyList: refusedRefs,
      quotas: null,
    };

    return { field, criterion };
  });
};

const buildJumpActions = (
  fieldId: string,
  defaultJump: string | undefined,
  choices: { ref: string; jump?: string }[],
  fieldIds: Map<string, string>,
): FieldJump[] => {
  const toAction = (jump: string | undefined): JumpAction | null => {
    if (jump === END_JUMP) {
      return { type: LogicJumpActionType.END_FORM };
    }
    const toFieldId = jump ? fieldIds.get(jump) : undefined;

    return toFieldId ? { type: LogicJumpActionType.JUMP, toFieldId } : null;
  };

  const refsByJump = new Map<string, string[]>();
  choices.forEach(({ ref, jump }) => {
    if (jump && toAction(jump)) {
      refsByJump.set(jump, [...(refsByJump.get(jump) || []), ref]);
    }
  });

  const actions: FieldJump[] = Array.from(refsByJump).map(
    ([jump, conditionChoiceRefs]) => ({
      id: uuidv4(),
      fieldId,
      ...(toAction(jump) as JumpAction),
      conditionChoiceRefs,
    }),
  );

  const defaultAction = toAction(defaultJump);
  if (defaultAction) {
    actions.push({ id: uuidv4(), fieldId, ...defaultAction });
  }

  return actions;
};
